// ─────────────────────────────────────────────────────────────────────────────
// Pulo de fila — as regras.
//
// Até a 2.0.4 a ordem da fila era só visual: qualquer analista puxava o 10º e
// deixava uma Alta estourada no topo, sem aviso e sem registro. Agora, ao
// coletar um ticket livre, o Orion conta quantos tickets livres estavam À
// FRENTE dele na MESMA ordem da tela de Filas (`ordenarPorSla`) e, havendo
// algum, pede justificativa — que fica gravada para a gerência apurar.
//
// Pegou o 10º? Pulou 9. A conta é a posição, não "quantos são mais graves".
//
// O QUE É "A FILA" AQUI. Só o que está esperando alguém AGORA:
//   · livre — dono 106 (Análise Suporte) ou sem dono, e fora de atendimento;
//   · e em espera — status 1 (Aguardando solução), ou Agendado (104) cuja
//     hora já chegou. Um agendado para amanhã não está esperando ninguém:
//     pegar outro na frente dele não é pulo.
//
// Também mora aqui a busca de tickets livres do MESMO CLIENTE, oferecidos
// depois da coleta para o analista puxar tudo de uma vez.
//
// Sem rede e sem Supabase, de propósito: roda sob `node` nos testes.
// ─────────────────────────────────────────────────────────────────────────────
import {
  ordenarPorSla, situacaoDoSla, slaDoTicket, donoDoTicket, OPERADOR_SEM_DONO,
} from './painelGerencia.js';
import { prioridadeDoTicket, PRIORIDADES } from './prioridade.js';

const STATUS_AGUARDANDO_SOLUCAO = 1;
const STATUS_AGENDADO = 104;
const STATUS_EM_ATENDIMENTO = 36;

/** Livre para qualquer um puxar: sem dono de fato e fora de atendimento. */
export function estaLivre(ticket) {
  if (!ticket) return false;
  if (Number(ticket.codigoStatusTicket) === STATUS_EM_ATENDIMENTO) return false;
  const dono = donoDoTicket(ticket);
  return dono == null || dono === 0 || dono === OPERADOR_SEM_DONO;
}

/** Está esperando alguém agora? (ver o cabeçalho) */
export function estaEmEspera(ticket, agora = new Date()) {
  const status = Number(ticket?.codigoStatusTicket);
  if (status === STATUS_AGUARDANDO_SOLUCAO) return true;
  if (status === STATUS_AGENDADO) {
    const hora = slaDoTicket(ticket);
    return !!hora && hora.getTime() <= agora.getTime();
  }
  return false;
}

/** A fila de entrada, na ordem da tela de Filas. */
export function filaDeEntrada(tickets, agora = new Date()) {
  return ordenarPorSla(
    (tickets || []).filter(t => estaLivre(t) && estaEmEspera(t, agora)),
    agora,
  );
}

/**
 * Quantos e quais tickets ficaram para trás ao coletar `alvo`.
 *
 * O alvo entra na ordenação mesmo quando não está na fila de entrada (um
 * agendado que ainda não venceu, por exemplo): o que conta é onde ELE cairia
 * na ordem. Devolve `null` quando o alvo não está livre — ticket do próprio
 * analista ou de colega não é coleta da fila, e a regra de dono já cuida dele.
 *
 * `{ posicao, pulados, total }` — `posicao` começa em 1; `pulados` está na
 * ordem da fila.
 */
export function analisarPulo(tickets, alvo, agora = new Date()) {
  if (!estaLivre(alvo)) return null;
  const codigo = Number(alvo.codigo);
  const fila = filaDeEntrada(tickets, agora).filter(t => Number(t.codigo) !== codigo);
  // EMPATE NÃO É PULO. Com a mesma prioridade e o mesmo SLA, `ordenarPorSla`
  // mantém a ordem em que a API devolveu — quem aparece primeiro na tela é
  // acaso. Conta só quem está ESTRITAMENTE à frente: com o alvo posto primeiro
  // num par, a ordenação (estável) só o tira do lugar se o outro vier antes.
  const pulados = fila.filter(t => Number(ordenarPorSla([alvo, t], agora)[0].codigo) !== codigo);
  return { posicao: pulados.length + 1, pulados, total: fila.length + 1 };
}

/**
 * A frase do aviso: "Há 9 tickets à frente, incluindo 2 Altas estouradas."
 *
 * Cita o que mais pesa na decisão: Alta fora do SLA, senão qualquer ticket
 * fora do SLA, senão Alta. Sem nenhum dos três, só a contagem.
 */
export function textoDoPulo(pulados, agora = new Date()) {
  const n = (pulados || []).length;
  if (!n) return '';
  const inicio = n === 1 ? 'Há 1 ticket à frente' : `Há ${n} tickets à frente`;

  const estourado = t => situacaoDoSla(t, agora) === 'estourado';
  const alta = t => prioridadeDoTicket(t) === 3;

  const altasEstouradas = pulados.filter(t => alta(t) && estourado(t)).length;
  if (altasEstouradas) {
    return `${inicio}, incluindo ${altasEstouradas === 1 ? 'uma Alta estourada' : `${altasEstouradas} Altas estouradas`}.`;
  }
  const estourados = pulados.filter(estourado).length;
  if (estourados) {
    return `${inicio}, incluindo ${estourados === 1 ? '1 fora do SLA' : `${estourados} fora do SLA`}.`;
  }
  const altas = pulados.filter(alta).length;
  if (altas) {
    return `${inicio}, incluindo ${altas === 1 ? 'uma Alta' : `${altas} Altas`}.`;
  }
  return `${inicio}.`;
}

/** O código do cliente do ticket, nos dois formatos da API. */
export function clienteDoRegistro(ticket) {
  const c = ticket?.codigoCliente ?? ticket?.cliente;
  const n = Number(c);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Os outros tickets LIVRES do mesmo cliente — em qualquer status livre, e não
 * só os em espera: um agendado do mesmo cliente também vale a pena resolver
 * na mesma ligação. Ordenados como a fila.
 */
export function ticketsDoMesmoCliente(tickets, alvo, agora = new Date()) {
  const cliente = clienteDoRegistro(alvo);
  if (!cliente) return [];
  const codigo = Number(alvo.codigo);
  return ordenarPorSla(
    (tickets || []).filter(t => Number(t.codigo) !== codigo
      && estaLivre(t)
      && clienteDoRegistro(t) === cliente),
    agora,
  );
}

/**
 * O retrato de um ticket pulado, como ele vai para o banco. É a foto do
 * momento: a gerência apura dias depois, quando o ticket já mudou.
 */
export function retratoDoTicket(t, agora = new Date()) {
  const sla = slaDoTicket(t);
  const prio = prioridadeDoTicket(t);
  return {
    codigo: Number(t.codigo),
    titulo: String(t.titulo || '').trim(),
    status: Number(t.codigoStatusTicket) || null,
    prioridade: prio ? PRIORIDADES[prio].rotulo : null,
    sla: sla ? sla.toISOString() : null,
    situacao_sla: situacaoDoSla(t, agora),
    cliente: clienteDoRegistro(t),
  };
}
